import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  AppBar,
  Toolbar,
  CircularProgress,
} from '@mui/material';
import { motion } from 'framer-motion';
import { CheckCircle, Logout } from '@mui/icons-material';
import { BicoButton } from '../components/BicoButton';
import { CameraViewer } from '../components/CameraViewer';
import { AjusteValorModal } from '../components/AjusteValorModal';
import { SucessoModal } from '../components/SucessoModal';
import { ConfirmarFinalizacaoModal } from '../components/ConfirmarFinalizacaoModal';
import { CardSurface } from '../../ui/CardSurface';
import { ButtonPrimary } from '../../ui/ButtonPrimary';
import { registrarBico } from './registrarBico';
import { finalizarAfericao } from './finalizarAfericao';
import { getAfericaoPorId } from '../offline/api';
import { useAuth } from '../auth/useAuth';

export function TelaAfericao({ empresa, afericaoId, onFinalizada }) {
  const { logout } = useAuth();
  
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };
  const [afericao, setAfericao] = useState(null);
  const [bicos, setBicos] = useState([]);
  const [selectedBico, setSelectedBico] = useState(null);
  const selectedBicoRef = useRef(null);
  const [showCamera, setShowCamera] = useState(false);
  const [showAjuste, setShowAjuste] = useState(false);
  const [showSucesso, setShowSucesso] = useState(false);
  const [showConfirmacao, setShowConfirmacao] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingAfericao, setLoadingAfericao] = useState(true);

  useEffect(() => {
    if (afericaoId) {
      loadAfericao(afericaoId);
    }
  }, [afericaoId]);

  const loadAfericao = async (id) => {
    setLoadingAfericao(true);
    try {
      const afericaoData = await getAfericaoPorId(id);
      setAfericao(afericaoData);
      
      const bicosMapeados = mapBicosFromAfericao(afericaoData, empresa);
      setBicos(bicosMapeados);
    } catch (error) {
      console.error('Erro ao carregar aferição:', error);
      alert('Erro ao carregar aferição. Tente novamente.');
    } finally {
      setLoadingAfericao(false);
    }
  };

  const mapBicosFromEmpresa = (empresa) => {
    const bicosList = [];
    
    if (empresa.bombas && Array.isArray(empresa.bombas)) {
      empresa.bombas.forEach((bomba) => {
        if (bomba.bicos && Array.isArray(bomba.bicos)) {
          bomba.bicos.forEach((bico) => {
            bicosList.push({
              bico,
              realizado: false,
              valor: null
            });
          });
        }
      });
    } else if (empresa.bicos && Array.isArray(empresa.bicos)) {
      empresa.bicos.forEach((bico) => {
        bicosList.push({
          bico,
          realizado: false,
          valor: null
        });
      });
    } else if (empresa.Bicos && Array.isArray(empresa.Bicos)) {
      empresa.Bicos.forEach((bico) => {
        bicosList.push({
          bico,
          realizado: false,
          valor: null
        });
      });
    }

    return bicosList;
  };

  const mapBicosFromAfericao = (afericao, empresa) => {
    const bicosList = mapBicosFromEmpresa(empresa);
    const afericoesBicos = afericao.afericoesBicos || afericao.AfericoesBicos || afericao.bicos || [];

    bicosList.forEach((item) => {
      const bicoId = item.bico.id || item.bico.Id;
      const afericaoBico = afericoesBicos.find(
        (ab) => (ab.bicoId || ab.BicoId) === bicoId
      );
      if (afericaoBico) {
        const finalizado = afericaoBico.finalizado || afericaoBico.Finalizado || false;
        item.realizado = finalizado;
        if (finalizado) {
          item.valor = afericaoBico.valor || afericaoBico.Valor || 0;
        }
      }
    });

    bicosList.sort((a, b) => {
      const numeroA = a.bico.numero || a.bico.Numero || a.bico.id || a.bico.Id || 0;
      const numeroB = b.bico.numero || b.bico.Numero || b.bico.id || b.bico.Id || 0;
      return numeroA - numeroB;
    });

    return bicosList;
  };

  const handleBicoClick = (bicoItem) => {
    if (bicoItem.realizado) {
      return;
    }
    setSelectedBico(bicoItem);
    selectedBicoRef.current = bicoItem;
    setShowCamera(true);
  };

  const handleCameraCapture = (blob) => {
    setCapturedImage(blob);
    setShowCamera(false);
    setShowAjuste(true);
  };

  const handleAjusteConfirm = useCallback(async (valor) => {
    const bicoAtual = selectedBicoRef.current;
    if (!bicoAtual) {
      console.error('Bico não selecionado');
      return;
    }
    
    setLoading(true);
    try {
      const numeroBico = bicoAtual.bico.numero || bicoAtual.bico.Numero || bicoAtual.bico.id || bicoAtual.bico.Id;
      const afericaoIdValue = afericao.id || afericao.Id;
      await registrarBico(
        afericaoIdValue,
        numeroBico,
        valor,
        capturedImage
      );

      await loadAfericao(afericaoIdValue);
    } catch (error) {
      console.error('Erro ao registrar bico:', error);
      alert('Erro ao registrar bico. Tente novamente.');
    } finally {
      setLoading(false);
      setShowAjuste(false);
      setSelectedBico(null);
      selectedBicoRef.current = null;
      setCapturedImage(null);
    }
  }, [afericao, capturedImage]);

  const handleFinalizar = async () => {
    // Calcular quantos bicos faltam ser aferidos
    const bicosAferidos = bicos.filter((item) => item.realizado).length;
    const bicosNaoAferidos = bicos.length - bicosAferidos;

    // Se houver bicos não aferidos, mostrar modal de confirmação
    if (bicosNaoAferidos > 0) {
      setShowConfirmacao(true);
      return;
    }

    // Se todos foram aferidos, finalizar diretamente
    await executarFinalizacao();
  };

  const executarFinalizacao = async () => {
    setLoading(true);
    try {
      const afericaoIdValue = afericao.id || afericao.Id;
      await finalizarAfericao(afericaoIdValue);
      
      // Mostrar modal de sucesso
      setShowSucesso(true);
    } catch (error) {
      console.error('Erro ao finalizar aferição:', error);
      alert('Erro ao finalizar aferição. Tente novamente.');
      setLoading(false);
    }
  };

  const handleConfirmarFinalizacao = async () => {
    setShowConfirmacao(false);
    await executarFinalizacao();
  };

  const handleSucessoClose = () => {
    setShowSucesso(false);
    if (onFinalizada) {
      onFinalizada();
    }
  };

  const bicosAferidos = bicos.filter((item) => item.realizado).length;
  const bicosNaoAferidos = bicos.length - bicosAferidos;
  const todosRealizados = bicos.length > 0 && bicos.every((item) => item.realizado);

  if (loadingAfericao) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (!afericao) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        <Typography color="error">Erro ao carregar aferição</Typography>
      </Box>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <AppBar position="sticky" elevation={0}>
        <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
          <Box>
            <Typography variant="h6" component="h1" fontWeight={600} color="text.primary">
              {empresa.nome || empresa.Nome || 'Aferição'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Turno: {afericao?.turno || afericao?.Turno || 'N/A'}
            </Typography>
          </Box>
          <Box
            component="button"
            onClick={handleLogout}
            sx={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: 'text.primary',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              p: 1,
              borderRadius: '12px',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.05)',
              },
            }}
          >
            <Logout sx={{ fontSize: 20 }} />
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ flex: 1, py: 3, px: { xs: 2, sm: 3 }, pb: { xs: 12, sm: 3 } }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: { xs: 1.5, sm: 2 },
              width: '100%',
            }}
          >
            {bicos.map((bicoItem, index) => {
              const bicoId = bicoItem.bico.id || bicoItem.bico.Id || index;
              
              return (
                <motion.div
                  key={bicoId}
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                  style={{ width: '100%', minWidth: 0 }}
                >
                  <BicoButton
                    bico={bicoItem.bico}
                    realizado={bicoItem.realizado}
                    valor={bicoItem.valor}
                    onClick={() => handleBicoClick(bicoItem)}
                  />
                </motion.div>
              );
            })}
          </Box>
        </motion.div>
      </Container>

      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          p: { xs: 2, sm: 3 },
          bgcolor: 'background.paper',
          borderTop: '1px solid rgba(0, 0, 0, 0.08)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          zIndex: 1000,
        }}
      >
        <Container maxWidth="lg">
          <ButtonPrimary
            fullWidth
            size="large"
            onClick={handleFinalizar}
            disabled={loading}
            startIcon={todosRealizados ? <CheckCircle /> : null}
            sx={{
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Processando...
              </Box>
            ) : (
              'Finalizar Aferição'
            )}
          </ButtonPrimary>
        </Container>
      </Box>

      {showCamera && (
        <CameraViewer
          onCapture={handleCameraCapture}
          onClose={() => {
            setShowCamera(false);
            setSelectedBico(null);
            selectedBicoRef.current = null;
            setCapturedImage(null);
          }}
        />
      )}

      {showAjuste && selectedBicoRef.current && (
        <AjusteValorModal
          valorInicial={selectedBicoRef.current?.valor || 0}
          onConfirm={handleAjusteConfirm}
          onCancel={() => {
            setShowAjuste(false);
            setSelectedBico(null);
            selectedBicoRef.current = null;
            setCapturedImage(null);
          }}
        />
      )}

      <SucessoModal
        open={showSucesso}
        onClose={handleSucessoClose}
        mensagem="Aferição registrada com sucesso!"
      />

      <ConfirmarFinalizacaoModal
        open={showConfirmacao}
        onClose={() => setShowConfirmacao(false)}
        onConfirm={handleConfirmarFinalizacao}
        bicosAferidos={bicosAferidos}
        bicosNaoAferidos={bicosNaoAferidos}
      />
    </Box>
  );
}
